import type { ComponentType } from "react";
import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import {
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleDashedIcon,
  GitBranchIcon,
  TimerIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { WorkItemProjectIcon } from "./WorkItemProjectIcon";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import { AgentBadge } from "./TaskRow";
import { formatDueDate, formatTaskDisplayId } from "./displayHelpers";
import { getDisplayStatus, getStatusConfig } from "./statusConfig";

interface TaskControlViewProps {
  items: readonly WorkItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

interface MetricCard {
  readonly label: string;
  readonly value: number;
  readonly tone: "neutral" | "green" | "amber" | "rose";
  readonly icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

function daysSince(value: string): number | null {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / 86_400_000));
}

function formatActivityAge(value: string): string {
  const age = daysSince(value);
  if (age === null) return "Unknown";
  if (age === 0) return "Today";
  if (age === 1) return "1d ago";
  return `${age}d ago`;
}

function buildMetrics(items: readonly WorkItem[]): readonly MetricCard[] {
  const inProgress = items.filter(
    (item) =>
      getDisplayStatus(item) === "in_progress" ||
      item.threadId !== null ||
      item.agentStatus === "running",
  );
  const needsReview = items.filter(
    (item) =>
      getDisplayStatus(item) === "in_review" ||
      item.agentStatus === "pending-approval" ||
      item.agentStatus === "pending-input",
  );
  const stale = items.filter((item) => {
    const age = daysSince(item.activityAt);
    return age !== null && age >= 14 && getDisplayStatus(item) !== "done";
  });
  return [
    {
      label: "Ready tasks",
      value: items.filter((item) => item.isReady && item.threadId === null).length,
      tone: "neutral",
      icon: CircleDashedIcon,
    },
    {
      label: "In progress",
      value: inProgress.length,
      tone: "green",
      icon: GitBranchIcon,
    },
    {
      label: "Review needed",
      value: needsReview.length,
      tone: "amber",
      icon: CheckCircle2Icon,
    },
    {
      label: "Stale open",
      value: stale.length,
      tone: "rose",
      icon: TimerIcon,
    },
  ];
}

const METRIC_TONE_CLASS: Record<MetricCard["tone"], string> = {
  neutral: "text-muted-foreground",
  green: "text-success-foreground",
  amber: "text-warning-foreground",
  rose: "text-hue-rose-foreground",
};

export function CommandCenterMetrics({ items }: { items: readonly WorkItem[] }) {
  return (
    <div className="@container/cc-metrics grid grid-cols-2 gap-2 @[680px]/cc-metrics:grid-cols-4">
      {buildMetrics(items).map((metric) => {
        const Icon = metric.icon;
        return (
          <div
            key={metric.label}
            className="rounded-md border border-border/55 bg-card/35 px-3 py-2.5"
          >
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex size-6 items-center justify-center rounded-md border border-border/60 bg-background/30",
                  METRIC_TONE_CLASS[metric.tone],
                )}
              >
                <Icon aria-hidden className="size-3.5" />
              </span>
              <span className="min-w-0 truncate text-[11px] font-medium text-muted-foreground/75">
                {metric.label}
              </span>
            </div>
            <div className="mt-2 font-mono text-[18px] font-semibold leading-none tabular-nums text-foreground">
              {metric.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }: { status: CmmdTaskStatus | string | null }) {
  const config = getStatusConfig(status);
  return (
    <span className="inline-flex h-5 w-fit max-w-full items-center gap-1.5 whitespace-nowrap rounded border border-border/55 bg-background/25 px-1.5 text-[11px] font-medium text-muted-foreground">
      <span className={cn("size-1.5 rounded-full", config.dotColor)} />
      {config.label}
    </span>
  );
}

function SourceLabel({ item }: { item: WorkItem }) {
  const label = [item.workspaceName, item.projectName].filter(Boolean).join(" / ");
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-[12px] font-medium text-muted-foreground/70">
      {/* The project is half of this label, so it carries its mark here too. */}
      {item.projectName ? (
        <WorkItemProjectIcon
          projectName={item.projectName}
          projectIcon={item.projectIcon}
          projectIconUrl={item.projectIconUrl}
        />
      ) : null}
      <span className="min-w-0 truncate">{label || item.source}</span>
    </span>
  );
}

export function TaskControlView({ items, selectedId, onSelect }: TaskControlViewProps) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden px-4 pb-4 sm:px-5 sm:pb-5">
      <div className="@container/control-list min-h-0 flex-1 overflow-hidden rounded-lg border border-border/55 bg-card/20">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-border/45 px-3 py-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground/55">
          <span>Task</span>
          <span className="text-right">Due</span>
        </div>
        <div role="list" className="h-full overflow-y-auto">
          {items.map((item) => {
            const displayId = formatTaskDisplayId(item.id);
            const isSelected = selectedId === item.id;
            const due = formatDueDate(item.dueDate);
            const activityAge = formatActivityAge(item.activityAt);
            return (
              <button
                key={item.id}
                type="button"
                role="listitem"
                data-selected={isSelected}
                aria-label={`Open task details for ${item.title}`}
                className={cn(
                  "group grid w-full min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 border-b border-border/35 px-3 py-3 text-left transition-[background-color,border-color,box-shadow] duration-150 ease-out last:border-b-0 hover:bg-muted/20 hover:shadow-[inset_2px_0_0_hsl(var(--ring)/0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring @[560px]/control-list:grid-cols-[104px_minmax(0,1fr)_auto] @[560px]/control-list:items-center @[560px]/control-list:gap-x-3",
                  isSelected &&
                    "border-border/55 bg-muted/30 shadow-[inset_2px_0_0_hsl(var(--ring)/0.75)]",
                )}
                onClick={() => onSelect(item.id)}
              >
                <div className="col-span-2 flex min-w-0 items-center gap-2 @[560px]/control-list:col-span-1 @[560px]/control-list:block">
                  <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/48">
                    {displayId}
                  </span>
                  <span className="hidden @[560px]/control-list:mt-1 @[560px]/control-list:block">
                    <StatusBadge status={item.status} />
                  </span>
                  <span className="@[560px]/control-list:hidden">
                    <StatusBadge status={item.status} />
                  </span>
                </div>

                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate text-[13px] font-semibold leading-5 text-foreground">
                      {item.title}
                    </span>
                    {item.blockedByCount > 0 ? (
                      <span className="shrink-0 rounded border border-hue-rose-border bg-hue-rose-surface px-1.5 py-px text-[11px] font-medium text-hue-rose-foreground">
                        {item.blockedByCount} blocker{item.blockedByCount === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-2">
                    <SourceLabel item={item} />
                    <AgentBadge agentStatus={item.agentStatus} agentProvider={item.agentProvider} />
                    <span className="hidden font-mono text-[11px] tabular-nums text-muted-foreground/50 @[1120px]/control-list:inline">
                      {activityAge}
                    </span>
                  </div>
                </div>

                <span className="flex items-center justify-end gap-2 self-center">
                  {due ? (
                    <span
                      className={cn(
                        "font-mono text-[11px] font-semibold tabular-nums",
                        due.warn ? "text-warning-foreground" : "text-muted-foreground/62",
                      )}
                    >
                      {due.label}
                    </span>
                  ) : (
                    <span className="font-mono text-[11px] text-muted-foreground/32">—</span>
                  )}
                  <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-border/45 bg-background/20 text-muted-foreground/45 transition-[border-color,color,background-color,transform] duration-150 ease-out group-hover:translate-x-0.5 group-hover:border-border/70 group-hover:bg-background/35 group-hover:text-foreground">
                    <ChevronRightIcon className="size-3.5" aria-hidden="true" />
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
